<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { ref, watch, computed } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import JoinGroup from './components/JoinGroup.vue'
import { useCommonStore } from '@/stores/modules/common'
import { formatNumberWithCommas, formatNumberWithWan } from '@/utils'
import Notice from './components/Notice.vue'
import { ElNotification, dayjs } from 'element-plus'
import {
  Coin,
  Connection,
  DataBoard,
  MessageBox,
  Present,
  Monitor,
  Odometer,
  Setting,
  ShoppingBag,
  Wallet,
  Collection,
  Star,
  Grape,
  MagicStick,
  ColdDrink,
  Brush,
  Reading,
  Files,
  TakeawayBox,
  Sunny,
} from '@element-plus/icons-vue'
import NoviceCheckIn from './components/NoviceCheckIn.vue'
import { getImportantNoticeApi } from '@/api/important-notice/'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import ActivityCheckIn from './components/ActivityCheckIn.vue'
import TestServerCdkey from './components/TestServerCdkey.vue'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()

const joinGroupShow = ref(false)
const noticeShow = ref(false)
const noviceCheckInShow = ref(false)
const activityShow = ref(false)

const importantNotice = ref('')
async function getImportantNotice() {
  const res = await getImportantNoticeApi(1)
  if (res) importantNotice.value = res
  if (envMode === 'test') importantNotice.value = '请勿对外爆料任何测试服新内容！'
}
function getPetOutsideReward() {
  const resetDiffDay = dayjs().diff(dayjs(userStore.outsidePetResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day')
  if (resetDiffDay < 1 || !userStore.petList?.length) return
  socketEmitAsync<number>({ type: 'getPetOutsideReward' }).then((res) => {
    if (res) {
      userStore.gold += res
      userStore.outsidePetResetTime = dayjs().format('YYYY-MM-DD')
      ElNotification({
        title: '魔物外出归来',
        message: `魔物为您带来了${formatNumberWithCommas(res)} 金币，已自动放入背包`,
        position: 'top-left',
        duration: 10000,
      })
    }
  })
}

const isVendorRefresh = computed(() => dayjs().diff(dayjs(userStore.vendorResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day') >= 1)
const isFreeBless = computed(
  () => dayjs().diff(dayjs(userStore.runeBlessResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day') >= 1 && userStore.soulType,
)

const testServerCdkeyShow = ref(false)

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return
    getImportantNotice()
    getPetOutsideReward()

    socketOn({
      type: 'getNoticeList',
      callBack: getImportantNotice,
    })
  },
)
</script>

<template>
  <div class="home-page page" v-bind="$attrs">
    <div class="top-bar" @click="commonStore.path = 'User'">
      <span>
        ✨<span class="nickname" style="font-weight: bold" :style="{ background: userStore.nicknameColor }">{{ userStore.nickname }}</span>
      </span>
      <div style="font-size: 12px">
        <span>💰 {{ formatNumberWithWan(userStore.gold) }}</span>
        <span style="margin-left: 12px">💎 {{ formatNumberWithWan(userStore.diamond) }}</span>
        <span style="margin-left: 12px" v-if="userStore.crystal || userStore.vipLevel">🔮 {{ formatNumberWithWan(userStore.crystal) }}</span>
      </div>
    </div>
    <el-alert
      :title="`通知：${importantNotice}`"
      type="warning"
      show-icon
      :closable="false"
      :style="{ visibility: importantNotice ? '' : 'hidden', margin: importantNotice ? '10px 0' : '5px 0' }"
    />
    <div class="home-btn-wrap">
      <CommonBtn type="primary" @done="joinGroupShow = true">
        <el-icon><Connection /></el-icon>
        <span>加入官方群</span>
      </CommonBtn>
      <CommonBtn type="success" @done="noticeShow = true">
        <el-icon><DataBoard /></el-icon>
        <span>公告栏</span>
      </CommonBtn>
      <CommonBtn type="primary" @done="commonStore.path = 'User'">
        <el-icon><Setting /></el-icon>
        <span>设置</span>
      </CommonBtn>
      <CommonBtn type="warning" @done="commonStore.path = 'Mall'">
        <el-icon><ShoppingBag /></el-icon>
        <span>商城</span>
      </CommonBtn>
      <el-badge :value="isVendorRefresh ? '新' : ''" :hidden="!isVendorRefresh" :offset="[-15, 0]" style="width: 100%">
        <CommonBtn
          type="success"
          @done="commonStore.path = 'BlackMarket'"
          :isLock="userStore.dailyChallengeLevel.diamond < 2"
          lockMsg="通过钻石挑战 2级开启黑市"
        >
          <el-icon><Wallet /></el-icon>
          <span>黑市</span>
        </CommonBtn>
      </el-badge>
      <CommonBtn
        type="success"
        @done="commonStore.path = 'Commerce'"
        :isLock="userStore.dailyChallengeLevel.gold < 5"
        lockMsg="通过金币挑战 5级开启商会"
      >
        <el-icon><Coin /></el-icon>
        <span>商会</span>
      </CommonBtn>
      <CommonBtn type="success" @done="commonStore.path = 'Warehouse'">
        <el-icon><MessageBox /></el-icon>
        <span>仓库</span>
      </CommonBtn>
      <CommonBtn type="success" @done="commonStore.path = 'Smithy'">
        <el-icon><Odometer /></el-icon>
        <span>铁匠铺</span>
      </CommonBtn>
      <CommonBtn type="success" @done="commonStore.path = 'Institute'">
        <el-icon><Monitor /></el-icon>
        <span>研究所</span>
      </CommonBtn>
      <CommonBtn type="success" @done="commonStore.path = 'EquipHandbook'">
        <el-icon><Collection /></el-icon>
        <span>图鉴</span>
      </CommonBtn>
      <CommonBtn type="primary" @done="commonStore.path = 'Treasury'">
        <el-icon><Files /></el-icon>
        <span>持有道具</span>
      </CommonBtn>
      <CommonBtn color="#b44c97" :dark="commonStore.isDarkMode" @done="commonStore.path = 'Pub'">
        <el-icon><ColdDrink /></el-icon>
        <span>酒馆</span>
      </CommonBtn>
      <CommonBtn type="success" @done="commonStore.path = 'OccultScience'" :isLock="!userStore.soulType" lockMsg="觉醒后开启秘术坊">
        <el-icon><MagicStick /></el-icon>
        <span>秘术坊</span>
      </CommonBtn>
      <CommonBtn
        color="#f7c99c"
        :dark="commonStore.isDarkMode"
        @done="commonStore.path = 'DarkGoldWorkshop'"
        :isLock="userStore.advanceLevel !== 4"
        lockMsg="进阶后开启暗金工坊"
      >
        <el-icon><TakeawayBox /></el-icon>
        <span>暗金工坊</span>
      </CommonBtn>
      <CommonBtn type="primary" @done="commonStore.path = 'Rank'">
        <el-icon><Reading /></el-icon>
        <span>奇闻</span>
      </CommonBtn>
      <template v-if="userStore.advanceLevel === 4">
        <CommonBtn
          color="#d4af37"
          :dark="commonStore.isDarkMode"
          @done="commonStore.path = 'SupremeWorkshop'"
          :isLock="userStore.peakLevel !== 8"
          lockMsg="二觉后开启至臻工坊"
          v-show="envMode === 'test' || envMode === 'dev'"
        >
          <el-icon><TakeawayBox /></el-icon>
          <span>至臻工坊</span>
        </CommonBtn>
        <CommonBtn
          color="#4bc1ff"
          :dark="commonStore.isDarkMode"
          @done="commonStore.path = 'MagicWorkshop'"
          :isLock="userStore.peakLevel !== 8"
          lockMsg="二觉后开启魔法工坊"
          v-show="envMode === 'test' || envMode === 'dev'"
        >
          <el-icon><MagicStick /></el-icon>
          <span>魔法工坊</span>
        </CommonBtn>
      </template>
      <CommonBtn type="danger" @done="noviceCheckInShow = true" style="grid-column: span 3" v-if="userStore.noviceCheckInIndex < 7">
        <el-icon><Star /></el-icon>
        <span>新手签到</span>
      </CommonBtn>
      <CommonBtn
        color="#626aef"
        @done="commonStore.path = 'PetRanch'"
        :dark="commonStore.isDarkMode"
        style="grid-column: span 3"
        :isLock="userStore.petList.length === 0 && userStore.dailyChallengeLevel.diamond < 7"
        lockMsg="拥有魔物或通过钻石挑战 7级后开启魔物牧场"
      >
        <el-icon><Grape /></el-icon>
        <span>魔物牧场</span>
      </CommonBtn>
      <el-badge :value="isFreeBless ? '免费祈福' : ''" :hidden="!isFreeBless" :offset="[-50, 0]" style="width: 100%; grid-column: span 3">
        <CommonBtn
          color="#fcc800"
          :dark="commonStore.isDarkMode"
          @done="commonStore.path = 'Awaken'"
          :isLock="userStore.eternalTowerLevel < 30 && !userStore.soulType"
          lock-msg="通过不朽之塔 30层开启觉醒殿堂"
        >
          <el-icon><Brush /></el-icon>
          <span>觉醒殿堂</span>
        </CommonBtn>
      </el-badge>
      <CommonBtn
        color="#ff4500"
        :dark="commonStore.isDarkMode"
        @done="commonStore.path = 'SecondAwaken'"
        :isLock="userStore.peakLevel !== 8"
        lock-msg="通过巅峰之路后开启二觉殿堂"
        style="grid-column: span 3"
        v-if="userStore.advanceLevel === 4"
        v-show="envMode === 'test' || envMode === 'dev'"
      >
        <el-icon><Brush /></el-icon>
        <span>二觉殿堂</span>
      </CommonBtn>
      <CommonBtn
        color="#2792c3"
        :dark="commonStore.isDarkMode"
        @done="commonStore.path = 'Relic'"
        :isLock="userStore.advanceLevel !== 4"
        lock-msg="进阶后开启圣物藏馆"
        style="width: 100%; grid-column: span 3"
      >
        <el-icon><Sunny /></el-icon>
        <span>圣物藏馆</span>
      </CommonBtn>
      <el-badge value="限时" :offset="[-28, 0]" style="width: 100%; grid-column: span 3" v-if="dayjs().isBefore('2026-01-31 00:00:00')">
        <CommonBtn color="#E63972" :dark="commonStore.isDarkMode" @done="activityShow = true">
          <el-icon><Present /></el-icon>
          <span>元旦签到</span>
        </CommonBtn>
      </el-badge>
      <el-badge
        value="签到"
        :hidden="!commonStore.canCheckIn"
        :offset="[-28, 0]"
        style="width: 100%; grid-column: span 3"
        v-if="dayjs().isBefore('2026-03-08 00:00:00')"
      >
        <CommonBtn color="#B22222 " type="danger" :dark="commonStore.isDarkMode" @done="commonStore.path = 'OneYear'">
          <el-icon><Present /></el-icon>
          <span>新春庆典</span>
        </CommonBtn>
      </el-badge>
      <CommonBtn
        type="primary"
        :dark="commonStore.isDarkMode"
        @done="testServerCdkeyShow = true"
        style="width: 100%; grid-column: span 3"
        v-if="dayjs().isBefore('2025-01-27') && envMode === 'prod'"
      >
        <el-icon><Present /></el-icon>
        <span>抽取测试服资格</span>
      </CommonBtn>
    </div>

    <el-dialog v-model="joinGroupShow" width="100%" style="max-width: 500px">
      <JoinGroup />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="joinGroupShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="noticeShow" title="公告栏" width="100%" style="max-width: 500px">
      <Notice />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="noticeShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="noviceCheckInShow" title="新手签到" width="100%" style="max-width: 500px">
      <NoviceCheckIn @close="noviceCheckInShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="noviceCheckInShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="activityShow" title="新春签到" width="100%" style="max-width: 500px" :destroy-on-close="false">
      <ActivityCheckIn @close="activityShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="activityShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="testServerCdkeyShow" title="测试服资格抽取" width="100%" style="max-width: 500px">
      <TestServerCdkey @close="testServerCdkeyShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="testServerCdkeyShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.home-page {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 5px;
    border-bottom: 1px var(--fontColor) solid;
  }
  .home-btn-wrap {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: grid;
    flex-flow: row wrap;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    .common-btn-wrap {
      :deep() button {
        width: 100%;
        i {
          font-size: 20px;
        }
        span {
          margin-left: 0;
        }
      }
    }
  }
}
</style>
